import { OdooService } from '@/api/odoo'
import { CustomComponentContext, CustomComponentContextType } from '@/utils/contexts/CustomComponentContext'

import { Input, InputDate, Select, Spinner, Switch } from 'alurkerja-ui'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import { timeOptions } from '@/utils/constants/timeOptions'
import { daysCount } from '@/utils/functions/PengajuanDinas/daysCount'
import { hoursCount } from '@/utils/functions/PengajuanDinas/hoursCount'

// eslint-disable-next-line react-refresh/only-export-components
export const specPengajuanDinasStartPilihJam = [
  {
    form_field_type: 'CUSTOM_COMPONENT',
    label: 'Tanggal Mulai Dinas',
    name: 'requestDateFrom',
    type: 'text',
  },
  {
    form_field_type: 'CUSTOM_COMPONENT',
    label: 'Tanggal Selesai Dinas',
    name: 'requestDateTo',
    type: 'text',
  },
  {
    form_field_type: 'CUSTOM_COMPONENT',
    label: 'Pilh Jam',
    name: 'customHours',
    type: 'text',
  },
  {
    form_field_type: 'CUSTOM_COMPONENT',
    label: 'Jam Mulai Dinas',
    name: 'requestHourFrom',
    type: 'text',
  },
  {
    form_field_type: 'CUSTOM_COMPONENT',
    label: 'Jam Selesai Dinas',
    name: 'requestHourTo',
    type: 'text',
  },
  {
    form_field_type: 'CUSTOM_COMPONENT',
    label: 'Durasi Dinas (Jam)',
    name: 'numberOfHours',
    type: 'text',
  },
  {
    form_field_type: 'CUSTOM_COMPONENT',
    label: 'Durasi Dinas (Hari)',
    name: 'numberOfDays',
    type: 'text',
  },
  {
    form_field_type: 'CUSTOM_COMPONENT',
    label: 'Jatah Cuti (Jam)',
    name: 'allocation_remaining_hours',
    type: 'text',
  },
  {
    form_field_type: 'CUSTOM_COMPONENT',
    label: 'Jatah Cuti (Hari)',
    name: 'allocation_remaining_days',
    type: 'text',
  },
]

export default function PengajuanDinasStartPilihJam() {
  const { setValue, taskData, control } = useContext<CustomComponentContextType>(CustomComponentContext)

  const defaultValueTime = {
    minTime: {
      label: taskData?.variables?.requestHourFrom,
      value: taskData?.variables?.requestHourFrom,
    },
    maxTime: {
      label: taskData?.variables?.requestHourTo,
      value: taskData?.variables?.requestHourTo,
    },
  }

  const defaultValueDate = {
    minDate: taskData?.variables?.requestDateFrom
      ? moment(taskData?.variables?.requestDateFrom, 'DD-MM-YYYY').format('YYYY-MM-DD')
      : null,
    maxDate: taskData?.variables?.requestDateTo
      ? moment(taskData?.variables?.requestDateTo, 'DD-MM-YYYY').format('YYYY-MM-DD')
      : null,
  }

  const [minDate, setMinDate] = useState<Date | null>(null)
  const [maxDate, setMaxDate] = useState<Date | null>(null)

  const [minTime, setMinTime] = useState<string | null>(null)
  const [maxTime, setMaxTime] = useState<string | null>(null)

  const [customHours, setCustomHours] = useState<boolean>(taskData?.variables?.customHours || false)
  const [duration, setDuration] = useState<number>(taskData?.variables?.numberOfHours || 0)
  const [durationDays, setDurationDays] = useState<number>(taskData?.variables?.numberOfDays || 0)

  const [minTimeOptions, setMinTimeOptions] = useState<{ label: string; value: string }[]>(
    timeOptions.slice(0, timeOptions.length - 1),
  )
  const [maxTimeOptions, setMaxTimeOptions] = useState<{ label: string; value: string }[]>(timeOptions.slice(1))

  useEffect(() => {
    if (!customHours && minDate && maxDate) {
      const { durationHours, durationDays } = daysCount(minDate, maxDate)

      setDurationDays(durationDays)
      setDuration(durationHours)

      setValue('numberOfDays', durationDays)
      setValue('numberOfHours', durationHours)

      setValue('requestHourFrom', '08:00')
      setValue('requestHourTo', '17:00')
      setValue('customHours', 'false')
    } else if (customHours && minTime && maxTime) {
      const {
        duration: durationFunc,
        durationDaysResult: durationDaysResultFunc,
        numberOfHours: numberOfHoursFunc,
      } = hoursCount(minTime, maxTime)

      setDuration(durationFunc)
      setValue('numberOfHours', numberOfHoursFunc)

      setValue('requestDateTo', moment(minDate).format('DD-MM-YYYY'))
      setDurationDays(Number(durationDaysResultFunc.toFixed(2)))
      setValue('numberOfDays', durationDaysResultFunc.toFixed(2))
    }
  }, [minDate, maxDate, customHours, minTime, maxTime, setValue])

  useEffect(() => {
    /**
     * Effect to filter min and max time options based on current date and time.
     * If the current date is the same as the min date, it will filter the time options
     * based on the current time.
     * If the current date is not the same as the min date, it will show all the time options.
     */
    if (moment(new Date()).isSame(moment(minDate), 'day')) {
      if (customHours) {
        setValue('requestHourFrom', undefined)
        setValue('requestHourTo', undefined)

        /**
         * Filter time options that are greater than the current time.
         * The filtered array will start from the current time and end at the second last element.
         */
        const minTimeOptionsTemp = timeOptions.filter(option => moment(new Date()).format('HH:mm') < option.value)
        /**
         * Filter time options that are less than the current time.
         * The filtered array will start from the second element and end at the last element.
         */
        const maxTimeOptionsTemp = minTimeOptionsTemp.slice(2)

        /**
         * Set the min time options and max time options based on the filtered arrays.
         * The min time options will start from the second element and end at the second last element.
         * The max time options will start from the second element and end at the last element.
         */
        setMinTimeOptions(minTimeOptionsTemp.slice(1, minTimeOptionsTemp.length - 1))
        setMaxTimeOptions(maxTimeOptionsTemp)
      }
    } else {
      setMinTimeOptions(timeOptions.slice(0, timeOptions.length - 1))
      setMaxTimeOptions(timeOptions.slice(1))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDate, customHours])

  const classNamesLabel = "after:content-['*'] after:text-red-400 after:text-sm font-medium"

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 space-x-2">
        <div>
          <label htmlFor="requestDateFrom" className={classNamesLabel}>
            Tanggal Mulai Dinas
          </label>
          <Controller
            name={'requestDateFrom'}
            control={control}
            rules={{ required: { value: true, message: `Tanggal Mulai Dinas is required.` } }}
            render={({ fieldState }) => {
              return (
                <>
                  <InputDate
                    id="requestDateFrom"
                    dateFormat={'dd MMMM yyyy'}
                    onChange={e => {
                      setValue('requestDateFrom', e && moment(e).format('DD-MM-YYYY'))
                      setMinDate(e ?? null)
                    }}
                    defaultValue={defaultValueDate?.minDate ? new Date(defaultValueDate?.minDate) : null}
                    minDate={new Date()}
                    autoComplete="off"
                  />
                  {fieldState.error && <p className="text-sm text-red-500">{fieldState.error.message}</p>}
                </>
              )
            }}
          ></Controller>
        </div>

        {!customHours && (
          <div>
            <label htmlFor="requestDateTo" className={classNamesLabel}>
              Tanggal Selesai Dinas
            </label>
            <Controller
              name={'requestDateTo'}
              control={control}
              rules={{ required: { value: true, message: `Tanggal Selesai Dinas is required.` } }}
              render={({ fieldState }) => {
                return (
                  <>
                    <InputDate
                      id="requestDateTo"
                      dateFormat={'dd MMMM yyyy'}
                      onChange={e => {
                        setValue('requestDateTo', e && moment(e).format('DD-MM-YYYY'))
                        setMaxDate(e ?? null)
                      }}
                      defaultValue={defaultValueDate?.maxDate ? new Date(defaultValueDate?.maxDate) : null}
                      minDate={minDate ?? new Date()}
                      autoComplete="off"
                    />
                    {fieldState.error && <p className="text-sm text-red-500">{fieldState.error.message}</p>}
                  </>
                )
              }}
            ></Controller>
          </div>
        )}
      </div>

      <div>
        <label htmlFor="customHours" className={classNamesLabel}>
          Pilih Jam
        </label>
        <Switch
          options={[
            { label: 'Ya', value: true },
            { label: 'Tidak', value: false },
          ]}
          onChange={e => {
            setValue('customHours', e as boolean)
            setCustomHours(e as boolean)
          }}
          defaultValue={taskData?.variables?.customHours ?? false}
        />
      </div>

      {customHours && (
        <div className="grid grid-cols-2 space-x-2">
          <div>
            <label id="label-requestHourFrom" htmlFor="requestHourFrom" className={classNamesLabel}>
              Jam Mulai Dinas
            </label>
            <Controller
              name={'requestHourFrom'}
              control={control}
              rules={{ required: { value: true, message: `Jam Mulai Dinas is required.` } }}
              render={({ fieldState }) => {
                return (
                  <>
                    <Select
                      inputId="requestHourFrom"
                      aria-labelledby="label-requestHourFrom"
                      options={minTimeOptions}
                      onChange={e => {
                        setValue('requestHourFrom', (e as { value: string }).value)
                        setMinTime((e as { value: string }).value as string)
                      }}
                      defaultValue={defaultValueTime.minTime}
                    />
                    {fieldState.error && <p className="text-sm text-red-500">{fieldState.error.message}</p>}
                  </>
                )
              }}
            ></Controller>
          </div>
          <div>
            <label id="label-requestHourTo" htmlFor="requestHourTo" className={classNamesLabel}>
              Jam Selesai Dinas
            </label>
            <Controller
              name={'requestHourTo'}
              control={control}
              rules={{ required: { value: true, message: `Jam Selesai Dinas is required.` } }}
              render={({ fieldState }) => {
                return (
                  <>
                    <Select
                      inputId="requestHourTo"
                      aria-labelledby="label-requestHourTo"
                      options={maxTimeOptions}
                      onChange={e => {
                        setValue('requestHourTo', (e as { value: string }).value)
                        setMaxTime((e as { value: string }).value as string)
                      }}
                      defaultValue={defaultValueTime.maxTime}
                    />
                    {fieldState.error && <p className="text-sm text-red-500">{fieldState.error.message}</p>}
                  </>
                )
              }}
            ></Controller>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 space-x-2">
        <div>
          <label htmlFor="numberOfHours" className={classNamesLabel}>
            Durasi Dinas (Jam)
          </label>
          <Controller
            name={'numberOfHours'}
            control={control}
            render={() => {
              return (
                <>
                  <Input
                    id="numberOfHours"
                    value={duration.toFixed(1)}
                    defaultValue={taskData?.variables?.numberOfHours ?? null}
                    disabled
                  />
                </>
              )
            }}
          ></Controller>
        </div>
        <div>
          <label htmlFor="numberOfDays" className={classNamesLabel}>
            Durasi Dinas (Hari)
          </label>
          <Controller
            name={'numberOfDays'}
            control={control}
            render={() => {
              return (
                <>
                  <Input
                    id="numberOfDays"
                    value={durationDays}
                    defaultValue={taskData?.variables?.numberOfDays ?? null}
                    disabled
                  />
                </>
              )
            }}
          ></Controller>
        </div>
      </div>
    </div>
  )
}
